import { Box, Checkbox, Group, Text, TextInput, type TreeNodeData, type UseTreeReturnType } from '@mantine/core';
import { useEffect, useMemo, useRef, useState, type RefObject, type SyntheticEvent } from 'react';
import { TreeNodeToggle } from './ExplorerParts';
import { isContainer, type JsonPath } from './json-path';
import { compareNames, escapePointerSegment } from './openapi-utils';
import VirtualTree from './VirtualTree';

type JsonNode = { name: string; path: JsonPath; value: unknown; depth: number };

/** Converts a JSON value into tree nodes, keyed by the escaped path to each value */
function toTreeData(value: unknown, parent: string, path: JsonPath, nodes: Map<string, JsonNode>): TreeNodeData[] {
  if (!isContainer(value)) return [];
  // Object keys are sorted by name so fields are easy to find, while array items keep their positions
  const entries: [string, string | number, unknown][] = Array.isArray(value)
    ? value.map((item, position) => [`[${position}]`, position, item])
    : Object.entries(value)
        .sort(([a], [b]) => compareNames(a, b))
        .map(([key, item]) => [key, key, item]);

  return entries.map(([name, key, child]) => {
    const childPath = [...path, key];
    const nodeValue = `${parent}/${escapePointerSegment(String(key))}`;
    nodes.set(nodeValue, { name, path: childPath, value: child, depth: path.length });
    return {
      value: nodeValue,
      label: name,
      children: isContainer(child) ? toTreeData(child, nodeValue, childPath, nodes) : undefined,
    };
  });
}

function SummaryLabel({ value }: { value: object }) {
  const count = Array.isArray(value) ? value.length : Object.keys(value).length;
  const noun = Array.isArray(value) ? (count === 1 ? 'item' : 'items') : count === 1 ? 'field' : 'fields';
  const [open, close] = Array.isArray(value) ? ['[', ']'] : ['{', '}'];
  return (
    <Text span size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
      {count === 0 ? `${open}${close}` : `${open} ${count} ${noun} ${close}`}
    </Text>
  );
}

const valueColor = (value: unknown) =>
  typeof value === 'string' ? 'green' : typeof value === 'number' ? 'blue' : 'orange';

function ValueLabel({ value }: { value: unknown }) {
  return (
    <Text span size="sm" ff="monospace" c={valueColor(value)} truncate>
      {JSON.stringify(value)}
    </Text>
  );
}

// Editors sit in a fixed width column against the right edge, so they line up at every depth
const EDITOR_WIDTH = 220;

// Keep clicks and key presses inside an input from reaching the tree, which would otherwise toggle or move focus
const stop = (event: SyntheticEvent) => event.stopPropagation();

function NumberEditor({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  // Edited as text so partial input such as "1." or "" can be typed without being overwritten
  const [draft, setDraft] = useState(String(value));
  useEffect(() => {
    setDraft((current) => (Number(current) === value && current.trim() !== '' ? current : String(value)));
  }, [value]);

  const parsed = Number(draft);
  const valid = draft.trim() !== '' && Number.isFinite(parsed);

  return (
    <TextInput
      size="xs"
      value={draft}
      error={!valid}
      styles={{
        input: { fontFamily: 'var(--mantine-font-family-monospace)', color: 'var(--mantine-color-blue-text)' },
      }}
      onChange={(event) => {
        const next = event.currentTarget.value;
        setDraft(next);
        if (next.trim() !== '' && Number.isFinite(Number(next))) onChange(Number(next));
      }}
      onBlur={() => setDraft(String(value))}
      onClick={stop}
      onKeyDown={stop}
    />
  );
}

function LeafEditorInput({ value, onChange }: { value: unknown; onChange: (value: unknown) => void }) {
  if (typeof value === 'number') return <NumberEditor value={value} onChange={onChange} />;
  if (typeof value === 'boolean') {
    return (
      <Checkbox
        size="xs"
        checked={value}
        label={String(value)}
        onChange={(event) => onChange(event.currentTarget.checked)}
        onClick={stop}
        onKeyDown={stop}
      />
    );
  }
  if (typeof value === 'string') {
    return (
      <TextInput
        size="xs"
        value={value}
        styles={{
          input: { fontFamily: 'var(--mantine-font-family-monospace)', color: 'var(--mantine-color-green-text)' },
        }}
        onChange={(event) => onChange(event.currentTarget.value)}
        onClick={stop}
        onKeyDown={stop}
      />
    );
  }
  return <ValueLabel value={value} />;
}

function LeafEditor({ value, onChange }: { value: unknown; onChange: (value: unknown) => void }) {
  return (
    <Box w={EDITOR_WIDTH} ml="auto" style={{ flexShrink: 0 }}>
      <LeafEditorInput value={value} onChange={onChange} />
    </Box>
  );
}

type JsonTreeProps = {
  value: unknown;
  tree: UseTreeReturnType;
  /** The element that scrolls the tree */
  scrollRef: RefObject<HTMLElement>;
  /** Makes leaf values editable. Only values can change, not the shape of the document. */
  onChange?: (path: JsonPath, value: unknown) => void;
  /** Nodes shallower than this start expanded, as do new nodes that appear later */
  expandDepth?: number;
  /** Containers matching this start collapsed, even when shallower than the expand depth */
  startCollapsed?: (value: object) => boolean;
};

/** Renders a JSON value as a collapsible tree of names and values */
function JsonTree({ value, tree, scrollRef, onChange, expandDepth = Infinity, startCollapsed }: JsonTreeProps) {
  const { data, nodes } = useMemo(() => {
    const nodeMap = new Map<string, JsonNode>();
    return { data: toTreeData(value, '', [], nodeMap), nodes: nodeMap };
  }, [value]);

  const { setExpandedState } = tree;

  // Nodes already given an expanded state. The tree marks every node it has not seen as collapsed when its data
  // changes, so it cannot tell new nodes apart from ones collapsed by hand.
  const knownNodes = useRef(new Set<string>());

  // New nodes open up to the expand depth, while anything toggled by hand keeps its state
  useEffect(() => {
    const known = knownNodes.current;
    setExpandedState((current) => {
      const next: Record<string, boolean> = {};
      nodes.forEach((node, key) => {
        if (!isContainer(node.value)) return;
        next[key] = known.has(key) ? current[key] === true : node.depth < expandDepth && !startCollapsed?.(node.value);
      });
      return next;
    });
    knownNodes.current = new Set(nodes.keys());
  }, [nodes, expandDepth, startCollapsed, setExpandedState]);

  return (
    <VirtualTree
      data={data}
      tree={tree}
      scrollRef={scrollRef}
      levelOffset="md"
      // Rows with editors are taller than plain values
      estimateRowHeight={onChange ? 34 : 28}
      renderNode={({ node, expanded, hasChildren, elementProps }) => {
        const info = nodes.get(node.value);
        if (!info) return null;
        return (
          <Group
            gap={6}
            wrap="nowrap"
            {...elementProps}
            className={`${elementProps.className} spec-tree-node`}
            onClick={(event) => {
              elementProps.onClick(event);
              if (hasChildren) tree.toggleExpanded(node.value);
            }}
          >
            <TreeNodeToggle hasChildren={hasChildren} expanded={expanded} />
            <Text span size="sm" ff="monospace" truncate miw={0} style={{ flexShrink: onChange ? 1 : 0 }}>
              {info.name}
              {!isContainer(info.value) && ':'}
            </Text>
            {isContainer(info.value) ? (
              <SummaryLabel value={info.value} />
            ) : onChange ? (
              <LeafEditor value={info.value} onChange={(next) => onChange(info.path, next)} />
            ) : (
              <ValueLabel value={info.value} />
            )}
          </Group>
        );
      }}
    />
  );
}

export default JsonTree;
